import React from 'react'
import {
  List,
  useTable,
  DeleteButton,
  DateField,
} from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderAvatarName } from '../../theme/columns'
import { ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { UsersRound, Image as ImageIcon, ScanLine } from '../../components/icons'

type Photo = Tables<'photos'>
type Profile = Tables<'profiles'>

// One row of the admin_photo_tags_overview view — the two kinds of photo tag
// (self-tags + nominations) unified, exposing WHO is tagged AND WHO tagged them.
interface TagOverview {
  id: string
  source: 'self_tag' | 'nomination'
  photo_id: string | null
  tagged_user_id: string | null
  tagged_by_user_id: string | null
  created_at: string
}

export function PhotoTagList() {
  const { tableProps } = useTable<TagOverview>({
    // The unified view of self-tags + nominations (admin-gated). Explicit so the
    // table reads the view rather than the bare photo_tags table.
    resource: 'admin_photo_tags_overview',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  })

  const rows = (tableProps.dataSource ?? []) as TagOverview[]

  // Ids referenced by the CURRENT page — bound the photo/member lookups to one
  // page via IN filters (never map over the whole tables).
  const photoIds = React.useMemo(
    () => Array.from(new Set(rows.map((t) => t.photo_id).filter((v): v is string => !!v))),
    [rows],
  )
  // Resolve BOTH the tagged member AND the tagger in one bounded profile query.
  const userIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .flatMap((t) => [t.tagged_user_id, t.tagged_by_user_id])
            .filter((v): v is string => !!v),
        ),
      ),
    [rows],
  )

  // Resolve photo_id -> photos.label (page-scoped via an IN filter).
  const { result: photosRes } = useList<Photo>({
    resource: 'photos',
    filters: [{ field: 'id', operator: 'in', value: photoIds }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: photoIds.length > 0 },
  })
  const photoMap = React.useMemo<Record<string, string>>(() => {
    const items = (photosRes?.data ?? []) as Photo[]
    return Object.fromEntries(items.map((p) => [p.id, p.label]))
  }, [photosRes])

  // Resolve user_id -> profiles.name (page-scoped, keyed on profiles.user_id).
  const { result: profilesRes } = useList<Profile>({
    resource: 'profiles',
    filters: [{ field: 'user_id', operator: 'in', value: userIds }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: userIds.length > 0 },
  })
  const memberMap = React.useMemo<Record<string, string>>(() => {
    const items = (profilesRes?.data ?? []) as Profile[]
    return Object.fromEntries(
      items.filter((p) => p.user_id != null).map((p) => [p.user_id as string, p.name]),
    )
  }, [profilesRes])

  // Bounded summary deck — one pre-aggregated row (now counts BOTH tag kinds).
  const { result: summaryRes } = useList<{
    total_tags: number | null
    distinct_photos: number | null
    distinct_members: number | null
  }>({
    resource: 'admin_photo_tags_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]
  const totalTags = summary?.total_tags ?? 0
  const taggedPhotos = summary?.distinct_photos ?? 0
  const taggedMembers = summary?.distinct_members ?? 0

  const stats: Stat[] = [
    { label: 'Total Tags', value: totalTags.toLocaleString(), icon: UsersRound, tone: 'gold' },
    { label: 'Tagged Photos', value: taggedPhotos.toLocaleString(), icon: ImageIcon, tone: 'jaguar' },
    { label: 'Members Tagged', value: taggedMembers.toLocaleString(), icon: ScanLine, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Content · Photos',
        description:
          'Every member tagged in a gallery photo — both members who tagged themselves and members tagged by someone else when a photo was uploaded. See who is tagged, who did the tagging, and remove a tag if it needs moderating.',
      }}
      stats={stats}
      statColumns={3}
    >
      <List resource="admin_photo_tags_overview" canCreate={false} title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey={(r: TagOverview) => `${r.source}:${r.id}`} scroll={{ x: true }}>
            <Table.Column<TagOverview>
              dataIndex="photo_id"
              title="Photo"
              render={(photoId: string | null) => (
                <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }}>
                  {photoId ? photoMap[photoId] ?? photoId : '—'}
                </span>
              )}
            />
            <Table.Column<TagOverview>
              dataIndex="tagged_user_id"
              title="Tagged Member"
              render={(userId: string | null) => renderAvatarName(userId ? memberMap[userId] ?? userId : '—')}
            />
            <Table.Column<TagOverview>
              dataIndex="tagged_by_user_id"
              title="Tagged By"
              render={(byId: string | null, record: TagOverview) =>
                record.source === 'self_tag' ? (
                  <span style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }}>
                    Self (tagged themselves)
                  </span>
                ) : (
                  renderAvatarName(byId ? memberMap[byId] ?? byId : '—')
                )
              }
            />
            <Table.Column<TagOverview>
              dataIndex="source"
              title="Type"
              render={(source: string) =>
                source === 'nomination' ? (
                  <Pill tone="jaguar">Tagged by member</Pill>
                ) : (
                  <Pill tone="neutral">Self-tag</Pill>
                )
              }
            />
            <Table.Column<TagOverview>
              dataIndex="created_at"
              title="Tagged At"
              sorter
              defaultSortOrder="descend"
              render={(value: string) => <DateField value={value} format="MMM D, YYYY h:mm A" />}
            />
            <Table.Column<TagOverview>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: TagOverview) => (
                <Space>
                  {/* Delete targets the row's REAL source table (self-tag vs nomination). */}
                  <DeleteButton
                    hideText
                    size="small"
                    resource={record.source === 'nomination' ? 'photo_tag_nominations' : 'photo_tags'}
                    recordItemId={record.id}
                  />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
